import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { ArrowRight } from "lucide-react"

function useTeam() {
  const [team, setTeam]       = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    fetch("/api/team_members")
      .then((r) => r.json())
      .then((data) => { setTeam(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])
  return { team, loading }
}

function useAwards() {
  const [awards, setAwards]   = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    fetch("/api/awards")
      .then((r) => r.json())
      .then((data) => { setAwards(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])
  return { awards, loading }
}

const values = [
  {
    title: "Restraint",
    desc: "We believe in editing ruthlessly. Every element must earn its place. The spaces we create have exactly what they need, and nothing more.",
  },
  {
    title: "Integrity",
    desc: "We work honestly — with our clients, our materials, and our ideas. No shortcuts. No compromises on quality when it matters.",
  },
  {
    title: "Curiosity",
    desc: "Each project begins with genuine inquiry. We question assumptions, explore the unexpected, and pursue ideas wherever they lead.",
  },
  {
    title: "Craft",
    desc: "Architecture is made — in the detail, the joint, the proportion, the finish. We take pride in the precision of how things are made.",
  },
]

export default function About() {
  const { team, loading: teamLoading } = useTeam()
  const { awards, loading: awardsLoading } = useAwards()
  return (
    <main className="pt-24 min-h-screen bg-stone-950">
      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1600&q=80&fit=crop"
          alt="Rafi Architecture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-stone-950/30" />
        <div className="absolute bottom-0 left-0 right-0 pb-12 px-6 lg:px-12">
          <div className="max-w-screen-2xl mx-auto">
            <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-4">
              <span className="w-6 h-px bg-amber-400" />
              Our Story
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-5xl lg:text-7xl font-light text-stone-100 leading-tight">
              About Bastu Binyash
            </h1>
          </div>
        </div>
      </div>

      {/* Philosophy */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="font-['Cormorant_Garamond'] text-4xl lg:text-5xl font-light text-stone-100 leading-tight mb-8">
              We design spaces<br />
              <span className="italic text-stone-400">worth inhabiting</span>
            </h2>
            <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed mb-6">
              Bastu Binyash was founded on a single belief: that architecture is not about buildings — it is about the people who use them, the communities they create, and the stories they hold.
            </p>
            <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed mb-6">
              Since 2006, we have built a practice rooted in rigorous inquiry and material honesty — working with private clients, developers, institutions, and municipalities across five continents.
            </p>
            <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed mb-10">
              We are architects, interior designers, urbanists, and visualizers — a team of 30+ specialists united by a shared commitment to design that is both beautiful and genuinely useful.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-amber-400 text-amber-400 px-8 py-4 font-['Space_Grotesk'] text-xs tracking-[0.2em] hover:bg-amber-400 hover:text-stone-950 transition-all duration-300 group"
            >
              WORK WITH US
              <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="h-64 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=500&q=80&fit=crop" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="h-64 overflow-hidden mt-8">
              <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500&q=80&fit=crop" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="h-48 overflow-hidden -mt-8">
              <img src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=500&q=80&fit=crop" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="h-48 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=500&q=80&fit=crop" alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="bg-stone-900 py-20 lg:py-28">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
          <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
            <span className="w-6 h-px bg-amber-400" />
            What We Stand For
          </span>
          <h2 className="font-['Cormorant_Garamond'] text-4xl lg:text-6xl font-light text-stone-100 mb-16">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <div key={v.title} className="border-l border-stone-700 pl-6">
                <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.3em] text-stone-600 uppercase mb-4 block">
                  0{i + 1}
                </span>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-stone-100 mb-4">
                  {v.title}
                </h3>
                <p className="font-['Inter'] text-sm text-stone-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
          <span className="w-6 h-px bg-amber-400" />
          The Team
        </span>
        <h2 className="font-['Cormorant_Garamond'] text-4xl lg:text-6xl font-light text-stone-100 mb-16">
          Meet the people<br />
          <span className="italic text-stone-400">behind the work</span>
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {teamLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-80 bg-stone-800 mb-5" />
                  <div className="h-4 bg-stone-800 w-1/2 mb-2" />
                  <div className="h-3 bg-stone-800 w-1/3 mb-4" />
                  <div className="h-3 bg-stone-800 w-full mb-1" />
                  <div className="h-3 bg-stone-800 w-4/5" />
                </div>
              ))
            : team.map((member) => (
                <div key={member.id} className="group">
                  <div className="overflow-hidden h-80 mb-5">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div>
                    <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-stone-100 mb-0.5">
                      {member.name}
                    </h3>
                    <p className="font-['Space_Grotesk'] text-[10px] tracking-[0.2em] text-amber-400 uppercase mb-3">
                      {member.role}
                    </p>
                    {member.bio && (
                      <p className="font-['Inter'] text-sm text-stone-500 leading-relaxed">{member.bio}</p>
                    )}
                  </div>
                </div>
              ))
          }
        </div>
      </div>

      {/* Awards */}
      <div className="bg-stone-900 py-20">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
          <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-10">
            <span className="w-6 h-px bg-amber-400" />
            Recognition
          </span>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {awardsLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="border border-stone-800 p-6 animate-pulse">
                    <div className="w-8 h-px bg-amber-400 mb-4" />
                    <div className="h-4 bg-stone-800 w-3/4 mb-3" />
                    <div className="h-2.5 bg-stone-800 w-1/3 mb-3" />
                    <div className="h-2.5 bg-stone-800 w-2/3" />
                  </div>
                ))
              : awards.map((item) => (
                  <div key={item.id} className="border border-stone-800 p-6 hover:border-amber-400/30 transition-colors">
                    <div className="w-8 h-px bg-amber-400 mb-4" />
                    <h4 className="font-['Cormorant_Garamond'] text-xl font-light text-stone-100 mb-1">{item.title}</h4>
                    {item.year && (
                      <p className="font-['Space_Grotesk'] text-[9px] tracking-[0.2em] text-stone-500 uppercase mb-2">{item.year}</p>
                    )}
                    {item.project && (
                      <p className="font-['Inter'] text-xs text-stone-600">{item.project}</p>
                    )}
                    {item.description && (
                      <p className="font-['Inter'] text-xs text-stone-600 mt-1">{item.description}</p>
                    )}
                  </div>
                ))
            }
          </div>
        </div>
      </div>
    </main>
  )
}
